import { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Button } from '../../../../../components/common/buttons/Button';
import { Icon } from '../../../../../components/common/icon/Icon';
import { pageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { SvgFileNameType } from '../../../../../interfaces/Element.interfaces';
import { IProductOption } from '../../../../../interfaces/Product.interfaces';

interface ICoreProductOption {
  iconName: SvgFileNameType;
  optionTitle: string;
  isActived: boolean;
}

const coreProductOptions: ICoreProductOption[] = [
  { iconName: 'AutoDriving', optionTitle: '자율주행', isActived: false },
  { iconName: 'WideTire', optionTitle: '광폭 타이어', isActived: false },
  { iconName: 'Rotary', optionTitle: '로터리', isActived: false },
  { iconName: 'MultiJoystick', optionTitle: '멀티 조이스틱', isActived: false },
  { iconName: 'ReverseFan', optionTitle: '역회전팬', isActived: false },
  { iconName: 'HydraulicToplink', optionTitle: '유압식 톱링크', isActived: false },
  { iconName: 'AutomaticCvt', optionTitle: '자동/무단 변속', isActived: false },
  { iconName: 'FrontPto', optionTitle: '전방 PTO', isActived: false },
  { iconName: 'CabinSuspension', optionTitle: '캐빈 서스펜션', isActived: false },
  { iconName: 'PowerBeyond', optionTitle: '파워비욘드벨브', isActived: false },
  { iconName: 'FrontLoader', optionTitle: '프론트로더', isActived: false },
  { iconName: 'SCR', optionTitle: 'SCR장치(요소수)', isActived: false },
];

interface ProductOptionSectionProps {
  activedProductOptions: IProductOption[];
}

export const ProductOptionSection = ({ activedProductOptions }: ProductOptionSectionProps) => {
  const { productId } = useParams();

  const navigate = useNavigate();

  const coreProductOptionsForDisplay = useMemo<ICoreProductOption[]>(() => {
    return coreProductOptions.map((coreProductOption) => {
      let isProductOptionActived = false;

      activedProductOptions.forEach((activedProductOption) => {
        if (activedProductOption.title === coreProductOption.optionTitle) {
          isProductOptionActived = true;
        }
      });

      const newProductOption = { ...coreProductOption, isActived: isProductOptionActived };
      return newProductOption;
    });
  }, [activedProductOptions]);

  return (
    <Container>
      <CoreProductOptionContainer>
        {coreProductOptionsForDisplay.map((coreProductOption) => (
          <CoreProductOption key={coreProductOption.optionTitle} isActived={coreProductOption.isActived}>
            <Icon name={coreProductOption.iconName} width={40} height={40} />
            <OptionTitle>{coreProductOption.optionTitle}</OptionTitle>
          </CoreProductOption>
        ))}
      </CoreProductOptionContainer>

      <Button
        size={'md'}
        borderType={'square'}
        color={'filledGreyLight'}
        text={{ size: 'md', content: '옵션 상세 보기' }}
        icon={{ fileName: 'AngleRight', position: 'right', size: 'Small', fill: '#858894' }}
        onClick={() => {
          if (!productId) {
            return;
          }
          ampli.pvmClickOptionDetailsButton();
          ReactGA.event('pvm_click_option_details_button');
          navigate(`${pageInfo.productOptionPage.path}/${productId}`);
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 24px;
`;

const CoreProductOptionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  align-self: stretch;
  flex-wrap: wrap;
`;

const CoreProductOption = styled.div<{ isActived: boolean }>`
  min-width: 110px;
  height: 70px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;

  opacity: ${(props) => (props.isActived ? 1 : 0.2)};
`;

const OptionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;
